import { useEffect, useMemo, useRef, useState } from "react";
import { FishIcon } from "../ui";
import type { LifelistEntry } from "../lib/types";
import {
  applyCategories,
  deleteTrip,
  removeBeachFromTrip,
  removeFromLifelist,
  renameTrip,
  setActiveTrip,
  useFishStore,
} from "../lib/storage";
import {
  getCachedFactsheet,
  setCachedFactsheet,
} from "../lib/factsheetCache";
import { rarityTier, RARITY_ORDER, RARITY_STYLE } from "../lib/rarity";
import { factsheet, recategorize } from "../lib/api";
import { navigate } from "../lib/router";
import { SpeciesHero } from "../components/SpeciesHero";
import { SpeciesDetail } from "../components/SpeciesDetail";
import { BeachPicker } from "../components/BeachPicker";
import {
  TripsOverviewMap,
  TRIP_COLORS,
  type OverviewPin,
} from "../components/TripsOverviewMap";
import { EndemicsCard } from "../components/EndemicsCard";
import { BadgeShelf } from "../components/BadgeShelf";
import { computeBadges } from "../../shared/badges";
import { toast } from "../components/Toaster";
import { confirmAction, promptText } from "../components/ConfirmModal";
import { findCuratedBeach, resolveBeachCoords } from "../../shared/beaches";
import { categoryBreakdown } from "../../shared/categories";
import { fmtDate } from "../lib/dates";

const OTHER = "Other";

// Plain-language meaning of each rarity tier, for the summary tiles' tooltip.
const TIER_MEANING: Record<string, string> = {
  Common: "Common — frequently seen near where it was logged",
  Uncommon: "Uncommon — seen now and then nearby",
  Rare: "Rare — seldom seen nearby",
};


// perf-options F9: virtualize Collections at 60+ entries.
//
// We considered a full virtualization library, but the pragmatic minimum
// is the CSS `content-visibility: auto` primitive. Below the viewport,
// the browser skips layout + paint for that subtree entirely — the
// initial render cost of the whole grid drops to roughly O(visible) even
// though the DOM technically holds every card. `contain-intrinsic-size`
// reserves the vertical space so scrollbar / scroll position stays sane.
// Zero JS, no library, no per-item observer.
//
// Silent no-op on Safari 17.5 and older (the property is undefined, so
// all cards paint eagerly as before). Applied only at 60+ entries so
// short lifelists carry no risk of hiding a paint bug.
const VIRTUALIZE_THRESHOLD = 60;

function HeroGrid({
  entries,
  chipsFor,
  onRemove,
  onOpen,
}: {
  entries: LifelistEntry[];
  chipsFor: (e: LifelistEntry) => string[];
  onRemove?: (e: LifelistEntry) => void;
  onOpen?: (e: LifelistEntry) => void;
}) {
  const virtualize = entries.length >= VIRTUALIZE_THRESHOLD;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {entries.map((e, i) => (
        <div
          key={e.taxon_id}
          style={
            // Skip the first row so above-the-fold cards paint eagerly and
            // don't ever pop in. Everything below the fold is fair game.
            virtualize && i >= 4
              ? {
                  contentVisibility: "auto",
                  containIntrinsicSize: "300px 240px",
                }
              : undefined
          }
        >
          <SpeciesHero
            photo_url={e.photo_url}
            common_name={e.common_name}
            scientific_name={e.scientific_name}
            observation_count={e.observation_count}
            chips={chipsFor(e)}
            onRemove={onRemove ? () => onRemove(e) : undefined}
            onOpen={onOpen ? () => onOpen(e) : undefined}
          />
        </div>
      ))}
    </div>
  );
}

// Trip-card action row. Delete used to sit inches from Rename on the
// surface — an accidental tap wiped an entire trip. Collapsed the two
// low-frequency actions into a ⋯ menu, kept "Set active" surfaced, and
// gave every control a 44 px tap target.
function TripCardActions({
  active,
  onSetActive,
  onRename,
  onDelete,
}: {
  active: boolean;
  onSetActive: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const items = Array.from(
          menuRef.current?.querySelectorAll<HTMLButtonElement>(
            'button[role="menuitem"]',
          ) ?? [],
        );
        if (items.length === 0) return;
        const active = document.activeElement as HTMLElement | null;
        const idx = items.findIndex((el) => el === active);
        const next = e.key === "ArrowDown"
          ? (idx + 1) % items.length
          : (idx - 1 + items.length) % items.length;
        items[next].focus();
      }
    };
    // Focus the first menu item on open so keyboard users don't land on
    // an empty menu with nothing focused.
    requestAnimationFrame(() => {
      const first = menuRef.current?.querySelector<HTMLButtonElement>(
        'button[role="menuitem"]',
      );
      first?.focus();
    });
    window.addEventListener("click", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {active ? (
        <button
          type="button"
          // Tapping the Active chip deactivates the trip (matches user
          // expectation — a control that looks like a button behaves
          // like one). Setting to null clears the active trip.
          onClick={() => setActiveTrip(null)}
          aria-pressed="true"
          title="Deactivate trip"
          className="inline-flex items-center rounded-full bg-aqua/15 px-3 py-2 text-xs font-semibold text-aqua ring-1 ring-inset ring-aqua/30 hover:bg-aqua/25 min-h-[44px]"
        >
          Active ✕
        </button>
      ) : (
        <button
          type="button"
          onClick={onSetActive}
          className="inline-flex items-center rounded-full px-3 py-2 text-xs font-semibold text-aqua-deep hover:bg-ink/5 min-h-[44px]"
        >
          Set active
        </button>
      )}
      <div className="relative">
        <button
          type="button"
          aria-label="Trip actions"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-full text-lg text-ink/60 hover:bg-ink/5"
        >
          ⋯
        </button>
        {open && (
          <div
            role="menu"
            ref={menuRef}
            onClick={(e) => e.stopPropagation()}
            className="absolute right-0 top-full z-20 mt-1 min-w-[160px] rounded-2xl bg-paper p-1 shadow-card ring-1 ring-inset ring-ink/10"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onRename();
              }}
              className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-ink hover:bg-ink/5 min-h-[44px]"
            >
              Rename
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
              className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-coral hover:bg-coral/10 min-h-[44px]"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Group({
  title,
  subtitle,
  entries,
  onOpen,
  children,
  beaches,
  onAddBeach,
  onRemoveBeach,
}: {
  title: string;
  subtitle?: string;
  entries: LifelistEntry[];
  onOpen?: (e: LifelistEntry) => void;
  children?: React.ReactNode;
  /** Named snorkel spots visited on this trip; renders a collapsible
   *  section above the species grid when non-empty. */
  beaches?: string[];
  /** Opens the beach picker for this trip. When provided we always show an
   *  affordance — collapsible chips when beaches exist, or a quiet
   *  "Add a beach" link when the trip has none yet. Old trips created
   *  before the feature landed hit this path too. */
  onAddBeach?: () => void;
  /** Called when the user taps a beach chip's × to remove it. */
  onRemoveBeach?: (name: string) => void;
}) {
  const hasBeaches = !!beaches && beaches.length > 0;
  return (
    <section className="card p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate font-display text-lg font-bold text-ink">
            {title}
          </h2>
          <p className="text-xs text-ink/60">
            {entries.length} species{subtitle ? ` · ${subtitle}` : ""}
          </p>
        </div>
        {children}
      </div>
      {hasBeaches && (
        <details className="group mb-3 overflow-hidden rounded-xl bg-paper ring-1 ring-inset ring-ring">
          <summary
            className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm font-semibold text-ink hover:bg-ink/[0.02]"
            style={{ listStyle: "none" }}
          >
            <span className="text-aqua-deep">◇</span>
            <span className="flex-1">Beaches · {beaches!.length}</span>
            <span className="text-xs text-meta transition-transform group-open:rotate-90">
              ▸
            </span>
          </summary>
          <div className="flex flex-wrap items-center gap-1.5 border-t border-hairline px-3 py-3">
            {beaches!.map((b) => (
              <span
                key={b}
                className="inline-flex min-h-[44px] items-center gap-1 rounded-full bg-ink/[0.06] pl-3 pr-1 text-[13px] font-semibold text-ink"
              >
                {b}
                {onRemoveBeach && (
                  <button
                    type="button"
                    onClick={() => onRemoveBeach(b)}
                    aria-label={`Remove ${b} from this trip`}
                    className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-full text-meta transition hover:bg-coral/10 hover:text-coral"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {onAddBeach && (
              <button
                type="button"
                onClick={onAddBeach}
                className="inline-flex min-h-[44px] items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold text-aqua-deep ring-1 ring-inset ring-aqua-deep/30 transition hover:bg-aqua-deep/10"
              >
                + Add beach
              </button>
            )}
          </div>
        </details>
      )}
      {!hasBeaches && onAddBeach && (
        <button
          type="button"
          onClick={onAddBeach}
          className="mb-3 flex min-h-[44px] w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-meta transition hover:bg-ink/[0.03]"
        >
          <span className="text-aqua-deep">◇</span>
          <span className="flex-1">Add a beach</span>
          <span className="text-aqua-deep">+</span>
        </button>
      )}
      <HeroGrid
        entries={entries}
        onOpen={onOpen}
        chipsFor={(e) =>
          [fmtDate(e.first_seen), e.category || OTHER].filter(Boolean)
        }
      />
    </section>
  );
}

function TypeBreakdown({ lifelist }: { lifelist: LifelistEntry[] }) {
  const [fixing, setFixing] = useState(false);

  const needFix = useMemo(
    () => lifelist.filter((e) => !e.category || e.category === OTHER),
    [lifelist],
  );

  const fix = async () => {
    setFixing(true);
    try {
      const { categories } = await recategorize({
        species: needFix.map((e) => ({
          taxon_id: e.taxon_id,
          scientific_name: e.scientific_name,
        })),
      });
      const n = applyCategories(categories);
      toast(
        n > 0
          ? `Recategorized ${n} species`
          : "Couldn't resolve any — try again later",
      );
    } catch (e) {
      toast(e instanceof Error ? e.message : "Recategorize failed");
    } finally {
      setFixing(false);
    }
  };

  const rows = useMemo(() => categoryBreakdown(lifelist), [lifelist]);

  if (lifelist.length === 0) return null;

  return (
    <section className="card p-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink/60">
          By fish type
        </h2>
        {needFix.length > 0 && (
          <button
            type="button"
            onClick={fix}
            disabled={fixing}
            className="inline-flex min-h-[44px] items-center justify-center rounded-full px-3 py-1 text-xs font-semibold text-aqua-deep ring-1 ring-inset ring-aqua-deep/30 hover:bg-aqua-deep/10 disabled:opacity-50"
          >
            {fixing ? "Fixing…" : `Fix ${needFix.length} Other`}
          </button>
        )}
      </div>
      <div className="mt-3 space-y-2">
        {rows.map((r) => (
          <div key={r.name}>
            <div className="mb-1 flex items-baseline justify-between text-sm">
              <span className="text-ink/70">{r.name}</span>
              <span className="tabular-nums text-ink/50">
                {r.n} · {r.pct}%
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-ink/10">
              <div
                className="h-full rounded-full bg-grad-cta"
                style={{ width: `${r.pct}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// Background-prefetch this many of the most-recent lifelist entries'
// factsheets when the page opens, so tapping any of them is instant.
// Anything past 12 isn't worth burning bandwidth on — the worker caches
// each call server-side anyway, so a colder tap is still a single fast
// round trip.
const PREFETCH_COUNT = 6;
// Skip prefetch entirely on Save-Data / metered connections. The worker
// still caches each cold factsheet server-side, so a colder tap is one
// fast round-trip — better than burning 6+ AI calls on someone on a boat.
function shouldPrefetchFactsheets(): boolean {
  if (typeof navigator === "undefined") return true;
  const conn = (navigator as unknown as {
    connection?: { saveData?: boolean; effectiveType?: string };
  }).connection;
  if (!conn) return true;
  if (conn.saveData) return false;
  return conn.effectiveType !== "slow-2g" && conn.effectiveType !== "2g";
}

export function CollectionsPage() {
  const lifelist = useFishStore((s) => s.lifelist);
  const trips = useFishStore((s) => s.trips);
  const visitedLocations = useFishStore((s) => s.visited_locations);
  const activeId = useFishStore((s) => s.active_trip_id ?? null);
  const [tab, setTab] = useState<"all" | "trip" | "location">("all");
  const [detail, setDetail] = useState<LifelistEntry | null>(null);

  // Idle-time prefetch of factsheets for the top N most-recent entries.
  // Depend on the actual top-N taxon ids so this doesn't re-fire on
  // every lifelist mutation (each fish add previously re-ran the whole
  // prefetch and could pay for AI calls the user never asked for). See
  // collections-factsheet-prefetch-refires-on-add.
  const topKey = useMemo(
    () =>
      lifelist
        .slice(0, PREFETCH_COUNT)
        .map((e) => e.taxon_id)
        .join(","),
    [lifelist],
  );
  useEffect(() => {
    if (topKey.length === 0) return;
    if (!shouldPrefetchFactsheets()) return;
    let cancelled = false;
    const targets = lifelist.slice(0, PREFETCH_COUNT);

    const step = (i: number) => {
      if (cancelled || i >= targets.length) return;
      const e = targets[i];
      if (getCachedFactsheet(e)) {
        step(i + 1);
        return;
      }
      factsheet({
        taxon_id: e.taxon_id,
        common: e.common_name,
        scientific: e.scientific_name,
      })
        .then((d) => {
          if (!cancelled) setCachedFactsheet(e, d);
        })
        .catch(() => {
          /* best-effort — a single failure shouldn't stop the rest */
        })
        .finally(() => step(i + 1));
    };

    const ric = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (h: number) => void;
      }
    ).requestIdleCallback;
    const cic = (
      window as unknown as { cancelIdleCallback?: (h: number) => void }
    ).cancelIdleCallback;

    let handle: number;
    if (ric) {
      handle = ric(() => step(0), { timeout: 4000 });
    } else {
      handle = window.setTimeout(() => step(0), 1500);
    }
    return () => {
      cancelled = true;
      if (ric && cic) cic(handle);
      else clearTimeout(handle);
    };
    // Re-prefetch when the user logs new species; depending on `lifelist`
    // itself would re-fire every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topKey]);
  const [beachPickerTripId, setBeachPickerTripId] = useState<string | null>(
    null,
  );

  const byTier = useMemo(() => {
    const counts = { Common: 0, Uncommon: 0, Rare: 0 };
    for (const e of lifelist) counts[rarityTier(e.observation_count)] += 1;
    return counts;
  }, [lifelist]);

  // Full derived badge set — milestones, group specialists, endemic regions,
  // rarity, signature sets, place/trip achievements. Recomputed from the
  // lifelist + trips; not persisted (the location-completion toast still runs
  // off state.badges in storage).
  const computedBadges = useMemo(() => {
    const tripSpeciesCount = new Map<string, number>();
    for (const t of trips) {
      tripSpeciesCount.set(
        t.id,
        lifelist.filter((e) => e.trip_ids.includes(t.id)).length,
      );
    }
    const beaches = [...new Set(trips.flatMap((t) => t.beaches ?? []))];
    // Region hints: curated beach regions + raw beach names + every visited or
    // planned location's name, so a region's endemic badges reveal as soon as
    // you've been there or planned a trip there.
    const regionHints = [
      ...trips
        .flatMap((t) => t.beaches ?? [])
        .map((b) => findCuratedBeach(b)?.region || ""),
      ...trips.flatMap((t) => t.beaches ?? []),
      ...visitedLocations.map((v) => v.name),
    ].filter(Boolean);
    return computeBadges({
      species: lifelist.map((e) => ({
        taxon_id: e.taxon_id,
        scientific_name: e.scientific_name,
        common_name: e.common_name,
        category: e.category,
        observation_count: e.observation_count,
        location_name: e.location_name,
      })),
      trips: trips.map((t) => ({
        beaches: t.beaches,
        created_at: t.created_at,
        species_count: tripSpeciesCount.get(t.id) ?? 0,
      })),
      beaches,
      regionHints,
    });
  }, [lifelist, trips, visitedLocations]);

  const tripName = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of trips) m.set(t.id, t.name);
    return m;
  }, [trips]);

  // Overview: for each trip that has beaches, plot every beach with
  // known coordinates as a pin, colour-coded per trip so you can read
  // "this dive was here, that dive was there" at a glance. Free-text
  // and un-coded beaches skip the map (still surfaced in the legend
  // as trip-level counts). Trips sorted most-recent-first so newer
  // dives get the earlier palette entries (which are more distinct).
  const beachOverview = useMemo(() => {
    const sortedTrips = [...trips].sort(
      (a, b) =>
        new Date(b.created_at).getTime() -
        new Date(a.created_at).getTime(),
    );
    const pins: OverviewPin[] = [];
    const legend: {
      id: string;
      name: string;
      color: string;
      totalBeaches: number;
      pinnedBeaches: number;
    }[] = [];
    let paletteIdx = 0;
    for (const t of sortedTrips) {
      const beaches = t.beaches ?? [];
      if (beaches.length === 0) continue;
      const color = TRIP_COLORS[paletteIdx % TRIP_COLORS.length];
      paletteIdx += 1;
      let pinned = 0;
      for (const beach of beaches) {
        const coords = resolveBeachCoords(beach, visitedLocations);
        if (coords) {
          pins.push({
            lat: coords.lat,
            lng: coords.lng,
            color,
            tripName: t.name,
            beachName: beach,
          });
          pinned += 1;
        }
      }
      legend.push({
        id: t.id,
        name: t.name,
        color,
        totalBeaches: beaches.length,
        pinnedBeaches: pinned,
      });
    }
    return { pins, legend };
  }, [trips, visitedLocations]);

  // Species surfaced beneath the map = everything logged on any trip
  // that has beaches. Sorted most-recently-seen first. Species from
  // trips without any beach fall through to a "no beaches yet" group
  // so the tab isn't empty for someone who hasn't tagged anything.
  const beachTripSpecies = useMemo(() => {
    const tripsWithBeaches = new Set(
      trips.filter((t) => (t.beaches?.length ?? 0) > 0).map((t) => t.id),
    );
    if (tripsWithBeaches.size === 0) return [];
    const ts = (e: LifelistEntry) =>
      new Date(e.last_seen || e.first_seen).getTime();
    return [...lifelist]
      .filter((e) => e.trip_ids.some((id) => tripsWithBeaches.has(id)))
      .sort((a, b) => ts(b) - ts(a));
  }, [trips, lifelist]);

  const untagged = useMemo(
    () => lifelist.filter((e) => e.trip_ids.length === 0),
    [lifelist],
  );

  // Flat list, most recently *seen* at the top (last_seen, then first_seen).
  // Backs the "All" tab.
  const allByRecent = useMemo(() => {
    const ts = (e: LifelistEntry) =>
      new Date(e.last_seen || e.first_seen).getTime();
    return [...lifelist].sort((a, b) => ts(b) - ts(a));
  }, [lifelist]);

  if (lifelist.length === 0 && trips.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <FishIcon className="h-10 w-10 text-aqua-deep" />
        <h1 className="mt-4 text-2xl font-bold text-ink">No collections yet</h1>
        <p className="mx-auto mt-2 max-w-sm text-ink/60">
          Start a trip on the Spot screen, then tap fish you see — they'll
          gather here by species, trip, and location.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate("/")}
          >
            Go spotting
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => navigate("/import")}
          >
            Import old trips
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-6 lg:max-w-5xl">
      <section className="card p-5">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-ink/60">
              Lifelist
            </p>
            <p className="font-display text-4xl font-bold text-ink">
              {lifelist.length}
              <span className="ml-2 text-base font-sans font-normal text-ink/50">
                species
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            {RARITY_ORDER.map((t) => (
              <div
                key={t}
                title={TIER_MEANING[t] ?? t}
                className={`rounded-xl px-3 py-2 text-center ${RARITY_STYLE[t].className}`}
              >
                <div className="text-lg font-bold tabular-nums">
                  {byTier[t]}
                </div>
                <div className="text-[10px] font-semibold uppercase tracking-wide">
                  {t}
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/import")}
          className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-ink/5 px-4 py-2 text-sm font-semibold text-ink/70 transition hover:bg-ink/10"
        >
          Import old trips from photos
        </button>
      </section>

      <BadgeShelf badges={computedBadges} />

      <EndemicsCard
        lifelist={lifelist}
        beachRegionTags={trips
          .flatMap((t) => t.beaches ?? [])
          .map((b) => findCuratedBeach(b)?.region || "")
          .filter(Boolean)}
        onOpenSpecies={(taxonId) => {
          const entry = lifelist.find((e) => e.taxon_id === taxonId);
          if (entry) setDetail(entry);
        }}
      />

      <TypeBreakdown lifelist={lifelist} />

      {/* Lagoon segmented control: one pill container, three tab buttons,
          active tab in aqua-deep. Replaces the legacy .seg / .seg-btn
          classes that were retired in the Tropical Vivid → Lagoon token
          collapse. */}
      <div
        role="tablist"
        aria-label="Collection view"
        className="inline-flex w-full rounded-full bg-paper p-1 shadow-card ring-1 ring-inset ring-ring sm:w-auto"
      >
        {(["all", "trip", "location"] as const).map((t) => {
          const on = tab === t;
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setTab(t)}
              className={`inline-flex min-h-[44px] flex-1 items-center justify-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua-deep sm:flex-none
                ${
                  on
                    ? "bg-aqua-deep text-paper"
                    : "text-meta hover:text-ink"
                }`}
            >
              {t === "all" ? "All" : `By ${t}`}
            </button>
          );
        })}
      </div>

      {tab === "all" && (
        <section className="card p-4">
          <p className="mb-3 text-xs text-ink/60">
            Your whole lifelist · {lifelist.length} species · most recently
            seen first
          </p>
          {lifelist.length === 0 ? (
            <p className="text-sm text-ink/55">Nothing logged yet.</p>
          ) : (
            <HeroGrid
              entries={allByRecent}
              onRemove={(e) => {
                void (async () => {
                  const ok = await confirmAction({
                    title: `Remove ${e.common_name}?`,
                    body: "Deletes it from your lifelist entirely — every trip and location it was logged on. This can't be undone.",
                    confirmLabel: "Remove",
                    destructive: true,
                  });
                  if (!ok) return;
                  removeFromLifelist(e.taxon_id);
                  toast(`Removed ${e.common_name} from your lifelist`);
                })();
              }}
              onOpen={setDetail}
              chipsFor={(e) => {
                const trips = e.trip_ids
                  .map((id) => tripName.get(id))
                  .filter((n): n is string => !!n);
                return [
                  e.category || OTHER,
                  ...(trips.length ? trips : ["no trip"]),
                  e.location_name || "unknown",
                ];
              }}
            />
          )}
        </section>
      )}

      {tab === "trip" && (
        <div className="space-y-4">
          {trips.length === 0 && (
            <p className="card p-4 text-sm text-ink/55">
              No trips yet. Create one from the “Logging to” bar on the Spot
              screen, then everything you tap joins it.
            </p>
          )}
          {trips.map((t) => {
            const entries = lifelist.filter((e) =>
              e.trip_ids.includes(t.id),
            );
            return (
              <Group
                key={t.id}
                title={t.name}
                subtitle={`created ${fmtDate(t.created_at)}`}
                entries={entries}
                beaches={t.beaches}
                onAddBeach={() => setBeachPickerTripId(t.id)}
                onRemoveBeach={(name) => removeBeachFromTrip(t.id, name)}
                onOpen={setDetail}
              >
                <TripCardActions
                  active={t.id === activeId}
                  onSetActive={() => setActiveTrip(t.id)}
                  onRename={async () => {
                    const name = await promptText({
                      title: "Rename trip",
                      body: "Give this trip a new name.",
                      defaultValue: t.name,
                      confirmLabel: "Rename",
                      placeholder: "Trip name",
                    });
                    if (name && name.trim()) renameTrip(t.id, name);
                  }}
                  onDelete={async () => {
                    const ok = await confirmAction({
                      title: `Delete trip “${t.name}”?`,
                      body: "Sightings are kept, just untagged.",
                      confirmLabel: "Delete",
                      destructive: true,
                    });
                    if (ok) deleteTrip(t.id);
                  }}
                />
              </Group>
            );
          })}
          {untagged.length > 0 && (
            <Group
              title="Not in a trip"
              entries={untagged}
              onOpen={setDetail}
            />
          )}
        </div>
      )}

      {tab === "location" && (
        <div className="space-y-4">
          {beachOverview.legend.length === 0 ? (
            <p className="card p-4 text-sm text-ink/55">
              No beaches logged yet. Open a trip and use the beach picker to
              mark the spots you visited — they'll show up here on a map,
              coloured by trip.
            </p>
          ) : (
            <>
              {beachOverview.pins.length > 0 ? (
                <TripsOverviewMap pins={beachOverview.pins} />
              ) : (
                <p className="card p-4 text-xs text-ink/55">
                  Your beach names aren't in the curated list, so we can't
                  plot them. Add coordinates to <code>src/shared/beaches.ts</code>{" "}
                  to see them on the map.
                </p>
              )}
              <section className="card p-4">
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink/60">
                  Trips
                </h3>
                <ul className="flex flex-wrap gap-2">
                  {beachOverview.legend.map((t) => (
                    <li
                      key={t.id}
                      className="inline-flex items-center gap-1.5 rounded-full bg-ink/5 px-2.5 py-1 text-xs text-ink"
                    >
                      <span
                        aria-hidden
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ background: t.color }}
                      />
                      <span className="font-semibold">{t.name}</span>
                      <span className="text-ink/50">
                        · {t.pinnedBeaches}/{t.totalBeaches} beaches
                        {t.pinnedBeaches < t.totalBeaches
                          ? " on map"
                          : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
              {beachOverview.pins.length > 0 && (
                <section className="card p-4">
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink/60">
                    Plan a return
                  </h3>
                  <p className="mb-3 text-xs text-ink/60">
                    Pick a spot to see likely species you haven't logged yet
                    and the next 7 days' snorkel windows.
                  </p>
                  <ul className="flex flex-wrap gap-2">
                    {[
                      ...new Map(
                        beachOverview.pins.map((p) => [p.beachName, p]),
                      ).values(),
                    ].map((p) => (
                      <li key={p.beachName}>
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/plan/${encodeURIComponent(
                                p.beachName.trim().toLowerCase(),
                              )}`,
                            )
                          }
                          className="inline-flex items-center gap-1.5 rounded-full bg-aqua-deep/10 px-3 py-1.5 text-xs font-semibold text-aqua-deep transition hover:bg-aqua-deep/20"
                        >
                          <span
                            aria-hidden
                            className="h-2 w-2 rounded-full"
                            style={{ background: p.color }}
                          />
                          Plan · {p.beachName}
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {beachTripSpecies.length > 0 && (
                <section className="card p-4">
                  <div className="mb-3 flex items-baseline justify-between gap-3">
                    <h2 className="font-display text-lg font-bold text-ink">
                      Fish from these trips
                    </h2>
                    <p className="text-xs text-ink/60">
                      {beachTripSpecies.length} species · most recently
                      seen first
                    </p>
                  </div>
                  <HeroGrid
                    entries={beachTripSpecies}
                    onOpen={setDetail}
                    chipsFor={(e) =>
                      [fmtDate(e.first_seen), e.category || OTHER].filter(
                        Boolean,
                      )
                    }
                  />
                </section>
              )}
            </>
          )}
        </div>
      )}

      <SpeciesDetail entry={detail} onClose={() => setDetail(null)} />

      {beachPickerTripId && (
        <BeachPicker
          tripId={beachPickerTripId}
          onClose={() => setBeachPickerTripId(null)}
        />
      )}

    </div>
  );
}
